import { useRef, useEffect, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  useWindowDimensions,
  Platform,
  Animated,
  StyleSheet,
  Image,
  Linking,
  Pressable,
} from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { Ionicons } from "@expo/vector-icons";
import { PublicNav } from "../../components/PublicNav";

// Module-level isWide used by the StyleSheet block at the bottom (runs at
// module load, can't read a React hook). Captured ONCE at first page load —
// styles that depend on it stay frozen across resize.
const { width: SCREEN_WIDTH } = Dimensions.get("window");
const isWide = SCREEN_WIDTH >= 768;

// Reactive hook called inside components so JSX (visibility, flexDirection,
// gap, etc.) re-renders on browser resize without reload.
function useIsWide(): boolean {
  return useWindowDimensions().width >= 768;
}

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const LIGHT_GRAY = "#F8F9FA";
const TEXT = "#1F2937";
const TEXT_MUTED = "#6B7280";

// ─── FadeIn wrapper ───────────────────────────────────────────────────────────
function FadeIn({ children, delay = 0, style }: { children: React.ReactNode; delay?: number; style?: object }) {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(20)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 600, delay, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 0, duration: 600, delay, useNativeDriver: true }),
    ]).start();
  }, []);
  return <Animated.View style={[{ opacity, transform: [{ translateY }] }, style]}>{children}</Animated.View>;
}

// ─── Nav Bar (matches landing page) ──────────────────────────────────────────
function NavBar() {
  const router = useRouter();
  const isWide = useIsWide();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeAnd = (fn: () => void) => { setMenuOpen(false); fn(); };

  return (
    <View style={styles.navbar}>
      <View style={styles.navInner}>
        <TouchableOpacity style={styles.navLogoContainer} onPress={() => router.push("/(auth)/")}>
          {Platform.OS === "web" ? (
            <img src="/logo-text.png" alt="Dash-Hound" style={{ height: 36, objectFit: "contain" } as any} />
          ) : (
            <Image source={require("../../assets/images/logo-main.png")} style={{ height: 40, width: 160 }} resizeMode="contain" />
          )}
        </TouchableOpacity>

        {isWide && (
          <View style={styles.navLinks}>
            <TouchableOpacity onPress={() => router.push("/(auth)/")}><Text style={styles.navLink}>Home</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/how-to-signup/")}><Text style={[styles.navLink, { color: ORANGE }]}>Join FAST CAT</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/blog/" as any)}><Text style={styles.navLink}>Blog</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/support/")}><Text style={styles.navLink}>Support</Text></TouchableOpacity>
          </View>
        )}

        <View style={styles.navActions}>
          {isWide ? (
            <>
              <TouchableOpacity onPress={() => router.push("/(auth)/login/")}><Text style={styles.navSignIn}>Sign In</Text></TouchableOpacity>
              <TouchableOpacity style={styles.navCTA} onPress={() => router.push("/(auth)/register/")}>
                <Text style={styles.navCTAText}>Get Started</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <TouchableOpacity style={styles.navCTA} onPress={() => router.push("/(auth)/register/")}>
                <Text style={styles.navCTAText}>Get Started</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setMenuOpen((v) => !v)}
                style={styles.navHamburger}
                accessibilityLabel={menuOpen ? "Close menu" : "Open menu"}
              >
                <Ionicons name={menuOpen ? "close" : "menu"} size={28} color={WHITE} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      {!isWide && menuOpen && (
        <>
          <Pressable style={styles.menuBackdrop} onPress={() => setMenuOpen(false)} />
          <View style={styles.mobileMenu}>
            <TouchableOpacity
              style={styles.mobileMenuItem}
              onPress={() => closeAnd(() => router.push("/(auth)/how-to-signup/"))}
            >
              <Text style={styles.mobileMenuItemTextAccent}>Join FAST CAT</Text>
              <Ionicons name="chevron-forward" size={18} color={ORANGE} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.mobileMenuItem} onPress={() => closeAnd(() => router.push("/(auth)/"))}>
              <Text style={styles.mobileMenuItemText}>Home</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.mobileMenuItem} onPress={() => closeAnd(() => router.push("/(auth)/blog/" as any))}>
              <Text style={styles.mobileMenuItemText}>Blog</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.mobileMenuItem} onPress={() => closeAnd(() => router.push("/(auth)/support/"))}>
              <Text style={styles.mobileMenuItemText}>Support</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.mobileMenuItem, { borderBottomWidth: 0 }]} onPress={() => closeAnd(() => router.push("/(auth)/login/"))}>
              <Text style={styles.mobileMenuItemText}>Sign In</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
}

// ─── Photo slot (supports real images or labeled placeholder) ────────────────
function Photo({ src, alt, caption, credit, creditUrl, aspectRatio = 16 / 9, width }: { src?: string; alt: string; caption?: string; credit?: string; creditUrl?: string; aspectRatio?: number; width?: number | string }) {
  const hasImage = !!src;
  const creditStyle = [styles.photoCaption, { fontSize: 11, fontStyle: "italic" as const, opacity: 0.7, marginTop: 2 }];
  return (
    <View style={{ marginVertical: 12, width: (width as any) ?? "100%" }}>
      <View style={[styles.photoFrame, { aspectRatio }]}>
        {hasImage ? (
          Platform.OS === "web" ? (
            <img src={src} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" } as any} />
          ) : (
            <Image source={{ uri: `https://dashhound.app${src}` }} style={{ width: "100%", height: "100%" }} resizeMode="cover" />
          )
        ) : (
          <View style={styles.photoPlaceholder}>
            <Text style={styles.photoPlaceholderIcon}>📷</Text>
            <Text style={styles.photoPlaceholderText}>{alt}</Text>
          </View>
        )}
      </View>
      {caption ? <Text style={styles.photoCaption}>{caption}</Text> : null}
      {credit ? (
        creditUrl ? (
          Platform.OS === "web" ? (
            <Text style={creditStyle as any}>📸 <a href={creditUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#F5A623", textDecoration: "underline" } as any}>{credit}</a></Text>
          ) : (
            <Text style={creditStyle as any} onPress={() => Linking.openURL(creditUrl)}>📸 <Text style={{ color: "#F5A623", textDecorationLine: "underline" }}>{credit}</Text></Text>
          )
        ) : (
          <Text style={creditStyle as any}>📸 {credit}</Text>
        )
      ) : null}
    </View>
  );
}

// ─── Expandable FAQ item ──────────────────────────────────────────────────────
function FAQ({ q, a, startOpen = true }: { q: string; a: React.ReactNode; startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <View style={styles.faqItem}>
      <TouchableOpacity onPress={() => setOpen(!open)} style={styles.faqHeader} activeOpacity={0.7}>
        <Text style={styles.faqQ}>{q}</Text>
        <Text style={styles.faqToggle}>{open ? "−" : "+"}</Text>
      </TouchableOpacity>
      {open ? <View style={styles.faqA}>{typeof a === "string" ? <Text style={styles.faqAText}>{a}</Text> : a}</View> : null}
    </View>
  );
}

// ─── Numbered step (for quick-start visual) ──────────────────────────────────
function StepCard({ num, title, body, delay }: { num: string; title: string; body: string; delay: number }) {
  const isWide = useIsWide();
  return (
    <FadeIn delay={delay} style={isWide ? styles.stepCardWide : styles.stepCard}>
      <View style={styles.stepBadge}>
        <Text style={styles.stepBadgeText}>{num}</Text>
      </View>
      <Text style={styles.stepTitle}>{title}</Text>
      <Text style={styles.stepBody}>{body}</Text>
    </FadeIn>
  );
}

// ─── Registration path card ──────────────────────────────────────────────────
function PathCard({ title, who, cost, url, note, delay }: { title: string; who: string; cost: string; url: string; note?: string; delay: number }) {
  const isWide = useIsWide();
  return (
    <FadeIn delay={delay} style={isWide ? styles.pathCardWide : styles.pathCard}>
      <View style={styles.pathCardTop}>
        <Text style={styles.pathTitle}>{title}</Text>
        <Text style={styles.pathWho}>{who}</Text>
        <View style={styles.pathCostBox}>
          <Text style={styles.pathCostLabel}>Cost</Text>
          <Text style={styles.pathCostVal}>{cost}</Text>
        </View>
        {note ? <Text style={styles.pathNote}>{note}</Text> : null}
      </View>
      <TouchableOpacity onPress={() => Linking.openURL(url)} style={styles.pathBtn}>
        <Text style={styles.pathBtnText}>Visit AKC →</Text>
      </TouchableOpacity>
    </FadeIn>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function HowToSignupScreen() {
  const router = useRouter();
  const isWide = useIsWide();

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const scrollToTarget = () => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = setTimeout(scrollToTarget, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: LIGHT_GRAY }}>
      {Platform.OS === "web" && (
        <Head>
          <title>How to Sign Up for Fast CAT — Beginner's Guide | Dash-Hound</title>
          <meta name="description" content="New to Fast CAT? Step-by-step guide to AKC registration paths, what to bring on event day, practice tips, what fun runs cost, and finding your first event near you." />
          <meta property="og:title" content="How to Sign Up for Fast CAT — Beginner's Guide" />
          <meta property="og:description" content="Step-by-step guide to AKC registration, event-day prep, practice tips, and finding your first Fast CAT event." />
          <meta property="og:url" content="https://dashhound.app/how-to-signup" />
          <meta name="twitter:title" content="How to Sign Up for Fast CAT — Beginner's Guide" />
          <meta name="twitter:description" content="Step-by-step guide to AKC registration, event-day prep, practice tips, and finding your first Fast CAT event." />
        </Head>
      )}
      <PublicNav />

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 0 }} showsVerticalScrollIndicator={false}>
        {/* HERO */}
        <View style={styles.hero}>
          <View style={StyleSheet.absoluteFillObject}>
            <View style={[StyleSheet.absoluteFillObject, { backgroundColor: NAVY_DARK }]} />
          </View>
          <FadeIn delay={0}>
            <Text style={styles.heroEyebrow}>GETTING STARTED</Text>
          </FadeIn>
          <FadeIn delay={120}>
            <Text style={styles.heroTitle}>Join <Text style={{ color: ORANGE }}>Fast CAT</Text></Text>
          </FadeIn>
          <FadeIn delay={240}>
            <Text style={styles.heroSub}>
              Everything you need to register your dog, find your first event, and start chasing lures. From AKC paperwork to day-of-event tips.
            </Text>
          </FadeIn>
        </View>

        {/* QUICK START STEPS */}
        <View style={styles.section}>
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionEyebrow}>QUICK START</Text>
            <Text style={styles.sectionTitle}>Five steps to the start line</Text>
            <Text style={styles.sectionSub}>
              If you're new to Fast CAT, this is the path from "I've never done this before" to "my dog just ran 25 mph."
            </Text>
          </FadeIn>

          <View style={isWide ? styles.stepsRowWide : styles.stepsCol}>
            <StepCard num="1" title="Pick registration path" body="Choose AKC, Canine Partners, PAL, or FSS based on your dog." delay={0} />
            <StepCard num="2" title="Register with AKC" body="Submit forms online. Get your dog's AKC number." delay={100} />
            <StepCard num="3" title="Find an event" body="Use the AKC event search or browse events in Dash-Hound." delay={200} />
            <StepCard num="4" title="Enter the event" body="Submit entry form and fee through the club's platform." delay={300} />
            <StepCard num="5" title="Show up & run" body="Bring rabies certificate, water, and a crate. Have fun." delay={400} />
          </View>

          {/* Two-column photo row: Rhea + Teton being released */}
          <View style={[isWide ? styles.photoRowWide : styles.photoRowCol, { maxWidth: 1100, alignSelf: "center", width: "100%", paddingHorizontal: 24, marginTop: 24 }]}>
            <View style={isWide ? { flex: 1 } : { width: "100%" }}>
              <Photo src="/join/rhea-launch.jpg" alt="Blue Heeler launching at a Fast CAT event" caption="Dash-Squad member Rhea being released" credit="Megan Johnson Photography" creditUrl="https://meganjohnsonphotographyllc.com/" aspectRatio={3 / 2} />
            </View>
            <View style={isWide ? { flex: 1 } : { width: "100%" }}>
              <Photo src="/join/teton-launch.jpg" alt="Blue Heeler launching at a Fast CAT event" caption="Dash-Squad member Teton being released" credit="Megan Johnson Photography" creditUrl="https://meganjohnsonphotographyllc.com/" aspectRatio={3 / 2} />
            </View>
          </View>
        </View>

        {/* REGISTRATION PATHS */}
        <View nativeID="registration" style={[styles.section, { backgroundColor: WHITE }]}>
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionEyebrow}>STEP 1 — REGISTRATION</Text>
            <Text style={styles.sectionTitle}>Which AKC path is right for your dog?</Text>
            <Text style={styles.sectionSub}>
              The AKC offers four ways to register your dog for performance events. The right one depends on whether your dog is purebred, has papers, or is a rescue.
            </Text>
          </FadeIn>

          <View style={isWide ? styles.pathsRow : styles.pathsCol}>
            <PathCard
              title="AKC Full Registration"
              who="Purebred dogs from AKC-registered litters. You have the paperwork from the breeder."
              cost="~$36.99 online"
              url="https://www.apps.akc.org/registration-dog/welcome"
              delay={0}
            />
            <PathCard
              title="AKC Canine Partners"
              who="Mixed breeds, rescues, or dogs of unknown parentage. The most popular path for Fast CAT."
              cost="$35 one-time"
              url="https://www.akc.org/register/information/canine-partners/enroll/"
              delay={100}
            />
            <PathCard
              title="AKC PAL"
              who="Purebred dogs without AKC papers — rescues, unregistered breeders, lost paperwork."
              cost="$35"
              note="Requires 2 photos (front + side) and spay/neuter proof."
              url="https://www.akc.org/register/information/purebred-alternative-listing-pal/"
              delay={200}
            />
            <PathCard
              title="FSS (Foundation Stock Service)"
              who="Rare breeds not yet fully recognized by the AKC."
              cost="Varies"
              url="https://www.akc.org/breeder-programs/foundation-stock-service-program/"
              delay={300}
            />
          </View>

        </View>

        {/* FAQ SECTION */}
        <View nativeID="faq" style={styles.section}>
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionEyebrow}>FREQUENTLY ASKED</Text>
            <Text style={styles.sectionTitle}>Everything you're wondering about</Text>
            <Text style={styles.sectionSub}>Tap any question to expand the answer.</Text>
          </FadeIn>

          <View style={styles.faqWrap}>
            <FAQ
              startOpen
              q="What is Fast CAT?"
              a={
                <View>
                  <Text style={styles.faqAText}>
                    Fast CAT (Coursing Ability Test) is a timed 100-yard dash. Your dog chases a plastic-bag lure pulled by a string across a straight track. Dogs run solo — speed is measured in mph, and points toward AKC titles are calculated using a breed-specific handicap.
                  </Text>
                  <Photo src="/join/rhea-running.jpg" alt="Blue Heeler chasing the lure at a Fast CAT event" caption="Dash-Squad member Rhea finishing her Fast CAT run" credit="Megan Johnson Photography" creditUrl="https://meganjohnsonphotographyllc.com/" aspectRatio={16 / 9} />
                </View>
              }
            />

            <FAQ
              startOpen
              q="Does my dog need papers to compete?"
              a="Nope. If your dog has no AKC papers — or is a rescue, mixed breed, or unknown mix — AKC Canine Partners is the easiest path. It's a $35 one-time enrollment that gives your dog a tracking number you can use to enter any AKC performance event, including Fast CAT."
            />

            <FAQ
              q="How much does it cost to get started?"
              a={
                <View>
                  <Text style={styles.faqAText}>Budget roughly <Text style={{ fontWeight: "700" }}>$55–$150</Text> to get your first event under your belt:</Text>
                  <View style={styles.costTable}>
                    <View style={styles.costTableRow}><Text style={styles.costTableLabel}>AKC registration</Text><Text style={styles.costTableVal}>$35–$37</Text></View>
                    <View style={styles.costTableRow}><Text style={styles.costTableLabel}>Event entry (per run)</Text><Text style={styles.costTableVal}>$20–$40</Text></View>
                    <View style={styles.costTableRow}><Text style={styles.costTableLabel}>Two runs at one event</Text><Text style={styles.costTableVal}>$35–$70</Text></View>
                    <View style={styles.costTableRow}><Text style={styles.costTableLabel}>Full weekend (4 runs)</Text><Text style={styles.costTableVal}>$80–$160</Text></View>
                  </View>
                  <Text style={[styles.faqAText, { fontStyle: "italic", color: TEXT_MUTED, marginTop: 6 }]}>
                    Prices vary by club and region. Some clubs offer member discounts or multi-run bundles.
                  </Text>
                </View>
              }
            />

            <FAQ
              q="What's the difference between PAL and Canine Partners?"
              a="Canine Partners is for dogs of any mix or unknown breed. PAL is specifically for purebred dogs that don't have AKC papers — you need to prove the dog is actually the breed you say it is, usually with 2 photos and a spay/neuter certificate. If you have a rescue that looks purebred, PAL is cheaper long-term because it unlocks breed-specific competitions. If you just want to do Fast CAT and other mixed events, Canine Partners is simpler."
            />

            <FAQ
              q="How do I find a Fast CAT event near me?"
              a={
                <View>
                  <Text style={styles.faqAText}>
                    Easiest: open the <Text style={styles.inlineLink} onPress={() => router.push("/(tabs)/events")}>Events tab in Dash-Hound</Text> — it lets you search by state or zip code and shows events within 100 miles. All the event data comes straight from the AKC event search, updated daily.
                  </Text>
                  <View style={{ alignItems: "center", marginTop: 8 }}>
                    <View style={{ width: "100%", maxWidth: 340 }}>
                      <Photo src="/join/events-phone.jpg" alt="Dash-Hound Events tab on iPhone" aspectRatio={1242 / 2153} caption="The Events tab in Dash-Hound" />
                    </View>
                  </View>
                </View>
              }
            />

            <FAQ
              q="How do I enter an event once I find one?"
              a={
                <View>
                  <Text style={styles.faqAText}>
                    Every event publishes a <Text style={{ fontWeight: "700" }}>premium list</Text> (PDF) with entry details, closing dates, and fees. Most clubs accept entries through online platforms:
                  </Text>
                  <Text style={styles.bullet}>• <Text style={styles.inlineLink} onPress={() => Linking.openURL("https://www.fastcatentries.com/")}>FastCatEntries.com</Text> — most common for Fast CAT</Text>
                  <Text style={styles.bullet}>• <Text style={styles.inlineLink} onPress={() => Linking.openURL("https://www.foytrialpro.com/")}>FoyTrialPro</Text> — common for multi-event trials</Text>
                  <Text style={styles.bullet}>• Paper entry by mail (older clubs still do this)</Text>
                  <Text style={[styles.faqAText, { marginTop: 8 }]}>You'll need your dog's AKC number, breed, DOB, owner info, and a payment method.</Text>
                </View>
              }
            />

            <FAQ
              q="What should I bring to my first event?"
              a={
                <View>
                  <Text style={styles.bullet}>• Current <Text style={{ fontWeight: "700" }}>rabies certificate</Text> (required by every club)</Text>
                  <Text style={styles.bullet}>• Entry confirmation email or printed receipt</Text>
                  <Text style={styles.bullet}>• Water + water bowl</Text>
                  <Text style={styles.bullet}>• Crate or x-pen for shade/rest</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>Two leashes</Text> — no retractable (most clubs ban them). Slip leashes are easier at the start line.</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>Bring a person with you</Text> — you'll want a catch/release helper if the club doesn't provide one. One person holds your dog at the start, the other catches at the finish.</Text>
                  <Text style={styles.bullet}>• Treats + poop bags</Text>
                  <Text style={styles.bullet}>• Camera! First runs are special</Text>
                </View>
              }
            />

            <FAQ
              q="How do I practice before the event?"
              a={
                <View>
                  <Text style={styles.faqAText}>
                    A little prep at home goes a long way — especially for first-timers. Fast CAT rewards prey drive, recall, and dogs who aren't spooked by the lure machine. Here's what handlers recommend:
                  </Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>Flirt pole or fur toy.</Text> Build chase drive by letting your dog go after a fur lure on a string. Short sessions, end on a win. This is the closest home practice to the real lure.</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>Practice the start + finish setup.</Text> Have a helper hold your dog while you walk 30-100 feet away. Call your dog excitedly — reward the sprint toward you with treats or their toy. That's exactly what happens at the start line.</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>Get them used to the lure noise.</Text> The lure motor can be loud and some dogs freeze the first time they hear it. Search <Text style={styles.inlineLink} onPress={() => Linking.openURL("https://www.youtube.com/results?search_query=akc+fast+cat+lure+machine+sound") }>"AKC Fast CAT lure" on YouTube</Text> and play it at home at increasing volume over a few days so they're not startled on race day.</Text>
                  <Text style={[styles.faqAText, { marginTop: 8 }]}>
                    Don't overtrain — Fast CAT is about instinct, not obedience. A handful of sessions is plenty.
                  </Text>
                </View>
              }
            />

            <FAQ
              q="How do titles work?"
              a={
                <View>
                  <Text style={styles.faqAText}>Dogs earn points based on speed, adjusted by a breed-specific handicap. Titles stack as you accumulate points:</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>BCAT</Text> — 150 points</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>DCAT</Text> — 500 points</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>FCAT</Text> — 1,000 points</Text>
                  <Text style={styles.bullet}>• <Text style={{ fontWeight: "700" }}>FCAT2–FCAT10+</Text> — every 500 points after FCAT</Text>
                  <Text style={[styles.faqAText, { marginTop: 8 }]}>Dash-Hound tracks all of this automatically — just log your runs and watch title progress fill in.</Text>
                </View>
              }
            />

            <FAQ
              q="Can puppies compete?"
              a="Dogs must be at least 12 months old on the day of the event. Under 12 months, they're not eligible to run, even for practice. Some clubs offer separate puppy-lure-coursing events for under-12-month dogs if you want to introduce them to the lure."
            />

            <FAQ
              q="What if my dog is nervous or new to lures?"
              a="Many clubs offer a practice / fun run day before regular events. Ask the club secretary — fun runs usually run $10–$15 per run depending on the club, and they're a great way to test the waters. Also, lots of dogs don't chase on their first try. Don't stress: try again at a later event. Some dogs 'click' after seeing a few other dogs run."
            />
          </View>
        </View>

        {/* PHOTO GALLERY / COMMUNITY */}
        <View style={[styles.section, { backgroundColor: WHITE, paddingVertical: isWide ? 60 : 40 }]}>
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionEyebrow}>THE EXPERIENCE</Text>
            <Text style={styles.sectionTitle}>What a day at Fast CAT looks like</Text>
          </FadeIn>

          <View style={isWide ? styles.galleryWide : styles.galleryCol}>
            <Photo src="/join/gallery-jim-mogwai.jpg" alt="Handler at the start with Mogwai before the run" aspectRatio={3 / 2} width={isWide ? 350 : "100%" as any} />
            <Photo src="/join/gallery-mogwai-running.jpg" alt="Mogwai chasing the lure at full speed" aspectRatio={3 / 2} width={isWide ? 350 : "100%" as any} />
            <Photo src="/join/gallery-teton-running.jpg" alt="Teton mid-stride down the Fast CAT course" aspectRatio={3 / 2} width={isWide ? 350 : "100%" as any} />
            <Photo src="/join/gallery-britt-toad.jpg" alt="Handler at the start line with Toad" aspectRatio={3 / 2} width={isWide ? 350 : "100%" as any} />
            <Photo src="/join/gallery-deana-toad.jpg" alt="Toad at the line with handler ready to release" aspectRatio={3 / 2} width={isWide ? 350 : "100%" as any} />
            <Photo src="/join/gallery-toad-puppy.jpg" alt="Toad as a puppy — every Fast CAT dog starts somewhere" aspectRatio={3 / 2} width={isWide ? 350 : "100%" as any} />
          </View>
        </View>

        {/* FORMS & LINKS */}
        <View style={styles.section}>
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionEyebrow}>FORMS & OFFICIAL LINKS</Text>
            <Text style={styles.sectionTitle}>All the official AKC resources</Text>
          </FadeIn>

          <View style={styles.linksGrid}>
            <TouchableOpacity style={styles.linkCard} onPress={() => Linking.openURL("https://www.apps.akc.org/registration-dog/welcome")}>
              <Text style={styles.linkCardTitle}>AKC Registration</Text>
              <Text style={styles.linkCardSub}>Full registration for purebred dogs with papers</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.linkCard} onPress={() => Linking.openURL("https://www.akc.org/register/information/canine-partners/enroll/")}>
              <Text style={styles.linkCardTitle}>AKC Canine Partners</Text>
              <Text style={styles.linkCardSub}>For mixed breeds and rescues</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.linkCard} onPress={() => Linking.openURL("https://www.akc.org/register/information/purebred-alternative-listing-pal/")}>
              <Text style={styles.linkCardTitle}>AKC PAL</Text>
              <Text style={styles.linkCardSub}>For purebreds without papers</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.linkCard} onPress={() => Linking.openURL("https://www.akc.org/breeder-programs/foundation-stock-service-program/")}>
              <Text style={styles.linkCardTitle}>AKC FSS</Text>
              <Text style={styles.linkCardSub}>Foundation Stock Service for rare breeds</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.linkCard} onPress={() => Linking.openURL("https://www.akc.org/sports/coursing/fast-cat/")}>
              <Text style={styles.linkCardTitle}>Fast CAT Rules & Titles</Text>
              <Text style={styles.linkCardSub}>Official rules, title requirements, handicaps</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.linkCard} onPress={() => router.push("/(tabs)/events")}>
              <Text style={styles.linkCardTitle}>Find Events in Dash-Hound</Text>
              <Text style={styles.linkCardSub}>All AKC Fast CAT events, updated daily in the app</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* CTA */}
        <View style={styles.ctaSection}>
          <FadeIn delay={0}>
            <Text style={styles.ctaTitle}>Ready to track your first run?</Text>
            <Text style={styles.ctaSub}>Sign up for Dash-Hound free and start logging runs, finding events, and climbing the leaderboard.</Text>
            <View style={styles.ctaBtns}>
              <TouchableOpacity style={styles.btnOrange} onPress={() => router.push("/(auth)/register/")}>
                <Text style={styles.btnOrangeText}>Get Started</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.btnOutline} onPress={() => router.push("/(auth)/support/")}>
                <Text style={styles.btnOutlineText}>Still have questions?</Text>
              </TouchableOpacity>
            </View>
          </FadeIn>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
          <View style={styles.footerLinks}>
            <TouchableOpacity onPress={() => router.push("/(auth)/privacy/")}><Text style={styles.footerLink}>Privacy</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/terms/")}><Text style={styles.footerLink}>Terms</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/support/")}><Text style={styles.footerLink}>Support</Text></TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  // ── Nav ─────────────────────────────────────────────────────────────────
  navbar: {
    backgroundColor: NAVY,
    paddingTop: Platform.OS === "ios" ? 44 : 0,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    // Creates a stacking context so the absolute-positioned mobile menu sits
    // above subsequent sections (hero, content) instead of behind them.
    position: "relative",
    zIndex: 100,
  },
  navInner: {
    maxWidth: 1200,
    width: "100%",
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    gap: 16,
  },
  navLogoContainer: { flexDirection: "row", alignItems: "center" },
  navLinks: { flexDirection: "row", gap: 28, flex: 1, justifyContent: "center" },
  navLink: { color: "rgba(255,255,255,0.85)", fontSize: 15, fontWeight: "500" },
  navActions: { flexDirection: "row", alignItems: "center", gap: 12 },
  navSignIn: { color: "rgba(255,255,255,0.85)", fontSize: 15, fontWeight: "500" },
  navCTA: { backgroundColor: ORANGE, borderRadius: 8, paddingHorizontal: 16, paddingVertical: 9 },
  navCTAText: { color: NAVY_DARK, fontSize: 14, fontWeight: "800" },
  navHamburger: { padding: 6, marginLeft: 4 },
  menuBackdrop: {
    ...(Platform.OS === "web"
      ? ({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 } as any)
      : StyleSheet.absoluteFillObject),
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 90,
  },
  mobileMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: NAVY_DARK,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    paddingVertical: 4,
    zIndex: 100,
  },
  mobileMenuItem: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.06)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  mobileMenuItemText: { color: WHITE, fontSize: 16, fontWeight: "500" },
  mobileMenuItemTextAccent: { color: ORANGE, fontSize: 16, fontWeight: "700" },

  // ── Hero ────────────────────────────────────────────────────────────────
  hero: {
    backgroundColor: NAVY_DARK,
    paddingTop: isWide ? 80 : 50,
    paddingBottom: isWide ? 90 : 60,
    paddingHorizontal: 24,
    alignItems: "center",
  },
  heroEyebrow: { color: ORANGE, fontSize: 13, fontWeight: "800", letterSpacing: 2, marginBottom: 12 },
  heroTitle: { color: WHITE, fontSize: isWide ? 56 : 38, fontWeight: "900", textAlign: "center", lineHeight: isWide ? 64 : 44 },
  heroSub: {
    color: "rgba(255,255,255,0.75)", fontSize: isWide ? 18 : 16, lineHeight: 28,
    textAlign: "center", marginTop: 18, maxWidth: 640,
  },

  // ── Sections ────────────────────────────────────────────────────────────
  section: {
    paddingVertical: isWide ? 80 : 50,
    paddingHorizontal: 24,
    backgroundColor: LIGHT_GRAY,
  },
  sectionHeader: { maxWidth: 720, alignSelf: "center", width: "100%", alignItems: "center", marginBottom: isWide ? 48 : 32 },
  sectionEyebrow: { color: ORANGE, fontSize: 12, fontWeight: "800", letterSpacing: 2, marginBottom: 10 },
  sectionTitle: { color: NAVY, fontSize: isWide ? 36 : 26, fontWeight: "900", textAlign: "center", lineHeight: isWide ? 44 : 34 },
  sectionSub: { color: TEXT_MUTED, fontSize: 16, lineHeight: 26, textAlign: "center", marginTop: 14, maxWidth: 580 },

  // ── Photo row (two-up at wide, stacked on mobile) ───────────────────────
  photoRowWide: { flexDirection: "row", gap: 16, alignItems: "flex-start" },
  photoRowCol: { flexDirection: "column", gap: 0 },
  // ── Steps ───────────────────────────────────────────────────────────────
  stepsCol: { maxWidth: 600, alignSelf: "center", width: "100%", gap: 14 },
  stepsRowWide: {
    flexDirection: "row", flexWrap: "wrap", gap: 16, justifyContent: "center",
    maxWidth: 1100, alignSelf: "center", width: "100%",
  },
  stepCard: { backgroundColor: WHITE, borderRadius: 14, padding: 20, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "flex-start" },
  stepCardWide: {
    backgroundColor: WHITE, borderRadius: 14, padding: 24,
    borderWidth: 1, borderColor: "#e5e7eb",
    width: 200, alignItems: "flex-start",
  },
  stepBadge: {
    width: 40, height: 40, borderRadius: 20, backgroundColor: ORANGE,
    alignItems: "center", justifyContent: "center", marginBottom: 14,
  },
  stepBadgeText: { color: NAVY_DARK, fontSize: 18, fontWeight: "900" },
  stepTitle: { fontSize: 16, fontWeight: "800", color: NAVY, marginBottom: 6 },
  stepBody: { fontSize: 14, color: TEXT_MUTED, lineHeight: 20 },

  // ── Paths ───────────────────────────────────────────────────────────────
  pathsCol: { maxWidth: 600, alignSelf: "center", width: "100%", gap: 14 },
  pathsRow: { flexDirection: "row", flexWrap: "wrap", gap: 18, justifyContent: "center", maxWidth: 1100, alignSelf: "center", width: "100%" },
  pathCard: { backgroundColor: WHITE, borderRadius: 14, padding: 22, borderWidth: 1, borderColor: "#e5e7eb", flexDirection: "column", justifyContent: "space-between" },
  pathCardWide: {
    backgroundColor: WHITE, borderRadius: 14, padding: 24,
    borderWidth: 1, borderColor: "#e5e7eb",
    width: 260,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  pathCardTop: { flex: 1 },
  pathTitle: { fontSize: 18, fontWeight: "800", color: NAVY, marginBottom: 8 },
  pathWho: { fontSize: 14, color: TEXT, lineHeight: 21, marginBottom: 14 },
  pathCostBox: { flexDirection: "row", alignItems: "baseline", justifyContent: "space-between", backgroundColor: LIGHT_GRAY, borderRadius: 8, padding: 10, marginBottom: 8 },
  pathCostLabel: { fontSize: 12, color: TEXT_MUTED, fontWeight: "600" },
  pathCostVal: { fontSize: 15, color: NAVY, fontWeight: "800" },
  pathNote: { fontSize: 12, color: TEXT_MUTED, fontStyle: "italic", marginBottom: 10 },
  pathBtn: { marginTop: 4, paddingVertical: 10, borderRadius: 8, backgroundColor: NAVY, alignItems: "center" },
  pathBtnText: { color: WHITE, fontWeight: "700", fontSize: 14 },

  // ── FAQ ─────────────────────────────────────────────────────────────────
  faqWrap: { maxWidth: 800, alignSelf: "center", width: "100%", gap: 10 },
  faqItem: { backgroundColor: WHITE, borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" },
  faqHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: 18 },
  faqQ: { flex: 1, fontSize: 16, fontWeight: "700", color: NAVY, paddingRight: 12 },
  faqToggle: { fontSize: 24, fontWeight: "300", color: ORANGE, width: 24, textAlign: "center" },
  faqA: { paddingHorizontal: 18, paddingBottom: 18, borderTopWidth: 1, borderTopColor: "#f3f4f6", paddingTop: 12 },
  faqAText: { fontSize: 15, color: TEXT, lineHeight: 24, marginBottom: 8 },
  bullet: { fontSize: 15, color: TEXT, lineHeight: 24, marginLeft: 4, marginBottom: 4 },
  inlineLink: { color: ORANGE, fontWeight: "600" },
  costTable: { marginTop: 8, borderRadius: 8, overflow: "hidden", borderWidth: 1, borderColor: "#e5e7eb" },
  costTableRow: { flexDirection: "row", justifyContent: "space-between", padding: 10, borderBottomWidth: 1, borderBottomColor: "#f3f4f6" },
  costTableLabel: { fontSize: 14, color: TEXT },
  costTableVal: { fontSize: 14, color: NAVY, fontWeight: "800" },

  // ── Photo ───────────────────────────────────────────────────────────────
  photoFrame: { width: "100%", borderRadius: 12, overflow: "hidden", backgroundColor: "#e5e7eb" },
  photoPlaceholder: { flex: 1, alignItems: "center", justifyContent: "center", borderWidth: 2, borderColor: "#d1d5db", borderStyle: "dashed", padding: 16 },
  photoPlaceholderIcon: { fontSize: 32, marginBottom: 6 },
  photoPlaceholderText: { color: TEXT_MUTED, fontSize: 13, fontStyle: "italic", textAlign: "center" },
  photoCaption: { fontSize: 13, color: TEXT_MUTED, textAlign: "center", marginTop: 8, fontStyle: "italic" },
  deviceRow: {
    flexDirection: isWide ? "row" : "column",
    gap: 20,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
  },
  deviceCol: { flex: isWide ? 1 : undefined, maxWidth: isWide ? 320 : 400, width: "100%" },
  devicePhoneCol: {
    width: isWide ? 300 : "100%",
    maxWidth: 400,
    flexShrink: 0,
  },
  deviceTabletStack: {
    width: isWide ? 440 : "100%",
    maxWidth: 500,
    flexShrink: 0,
    flexDirection: "column",
  },
  deviceCaption: { fontSize: 13, color: TEXT_MUTED, textAlign: "center", marginTop: 4, fontStyle: "italic" },

  // ── Gallery ─────────────────────────────────────────────────────────────
  galleryCol: { maxWidth: 600, alignSelf: "center", width: "100%", gap: 12 },
  galleryWide: { flexDirection: "row", flexWrap: "wrap", gap: 14, maxWidth: 1100, alignSelf: "center", width: "100%", justifyContent: "center" },

  // ── Links grid ──────────────────────────────────────────────────────────
  linksGrid: { flexDirection: "row", flexWrap: "wrap", gap: 14, maxWidth: 1000, alignSelf: "center", width: "100%", justifyContent: "center" },
  linkCard: {
    backgroundColor: WHITE, borderRadius: 12, padding: 18,
    borderWidth: 1, borderColor: "#e5e7eb",
    width: isWide ? 290 : "100%",
  },
  linkCardTitle: { fontSize: 16, fontWeight: "800", color: NAVY, marginBottom: 4 },
  linkCardSub: { fontSize: 13, color: TEXT_MUTED, lineHeight: 18 },

  // ── CTA ─────────────────────────────────────────────────────────────────
  ctaSection: { backgroundColor: NAVY, paddingVertical: isWide ? 80 : 50, paddingHorizontal: 24, alignItems: "center" },
  ctaTitle: { color: WHITE, fontSize: isWide ? 32 : 24, fontWeight: "900", textAlign: "center" },
  ctaSub: { color: "rgba(255,255,255,0.7)", fontSize: 16, lineHeight: 26, textAlign: "center", marginTop: 14, maxWidth: 520 },
  ctaBtns: { flexDirection: "row", flexWrap: "wrap", justifyContent: "center", gap: 12, marginTop: 24 },
  btnOrange: { backgroundColor: ORANGE, paddingHorizontal: 28, paddingVertical: 14, borderRadius: 10 },
  btnOrangeText: { color: NAVY_DARK, fontWeight: "800", fontSize: 16 },
  btnOutline: { borderWidth: 2, borderColor: "rgba(255,255,255,0.3)", paddingHorizontal: 28, paddingVertical: 12, borderRadius: 10 },
  btnOutlineText: { color: WHITE, fontWeight: "700", fontSize: 16 },

  // ── Footer ──────────────────────────────────────────────────────────────
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, paddingHorizontal: 24, alignItems: "center" },
  footerText: { color: "rgba(255,255,255,0.4)", fontSize: 13 },
  footerLinks: { flexDirection: "row", gap: 20, marginTop: 12 },
  footerLink: { color: "rgba(255,255,255,0.6)", fontSize: 13 },
});
